import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, switchMap, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { ReCaptchaAction } from '../../enums/recaptcha-action';
import { ApiConfigService } from '../../services/api-config/api-config.service';

declare const grecaptcha: any;

@Injectable()
export class RecaptchaInterceptor implements HttpInterceptor {
  constructor(private apiConfigService: ApiConfigService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.apiConfigService.recaptchaEnabled) {
      return next.handle(request);
    }

    const actionName = this.getActionNameFromUrl(request.url);

    if (!actionName) {
      return next.handle(request);
    }

    return this.executeRecaptcha(actionName).pipe(
      timeout(5000),
      catchError((error) => {
        return throwError(
          () =>
            new HttpErrorResponse({
              error: {
                message: 'ReCaptcha verification failed. Please try again.',
              },
              status: 0,
              statusText: 'ReCaptcha Error',
              url: request.url,
            })
        );
      }),
      switchMap((token) => {
        const modifiedRequest = request.clone({
          setHeaders: {
            'X-ReCaptcha-Token': token,
          },
        });
        return next.handle(modifiedRequest);
      })
    );
  }

  private executeRecaptcha(action: string): Observable<string> {
    return new Observable<string>((observer) => {
      if (typeof grecaptcha === 'undefined' || !grecaptcha) {
        observer.error('ReCaptcha not loaded');
        observer.complete();
        return;
      }

      const siteKey = this.apiConfigService.recaptchaSiteKey;

      if (!siteKey) {
        observer.error('ReCaptcha site key not configured');
        observer.complete();
        return;
      }

      try {
        grecaptcha.ready(() => {
          try {
            grecaptcha
              .execute(siteKey, { action })
              .then((token: string) => {
                observer.next(token);
                observer.complete();
              })
              .catch((error: any) => {
                observer.error(error || 'ReCaptcha execution failed');
                observer.complete();
              });
          } catch (error) {
            observer.error(error || 'ReCaptcha execution error');
            observer.complete();
          }
        });
      } catch (error) {
        observer.error(error || 'ReCaptcha ready error');
        observer.complete();
      }
    });
  }

  private getActionNameFromUrl(url: string): string | null {
    const apiUrl: string = url.split('/').pop()?.replace(/-/g, '') || '';

    if (apiUrl === ReCaptchaAction.login.toLowerCase()) {
      return ReCaptchaAction.login;
    } else if (apiUrl === ReCaptchaAction.loginConfirm.toLowerCase()) {
      return ReCaptchaAction.loginConfirm;
    } else if (apiUrl === ReCaptchaAction.register.toLowerCase()) {
      return ReCaptchaAction.register;
    } else if (apiUrl === ReCaptchaAction.registerConfirm.toLowerCase()) {
      return ReCaptchaAction.registerConfirm;
    } else if (apiUrl === ReCaptchaAction.activation.toLowerCase()) {
      return ReCaptchaAction.activation;
    } else if (apiUrl === ReCaptchaAction.forgotPswSendCode.toLowerCase()) {
      return ReCaptchaAction.forgotPswSendCode;
    } else if (apiUrl === ReCaptchaAction.forgotPswVerifyCode.toLowerCase()) {
      return ReCaptchaAction.forgotPswVerifyCode;
    } else if (apiUrl === ReCaptchaAction.forgotPswReset.toLowerCase()) {
      return ReCaptchaAction.forgotPswReset;
    } else {
      return null;
    }
  }
}
